import React from 'react';
import { cn } from '@/lib/utils';

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: 'default' | 'destructive' | 'success' | 'warning' | 'info';
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, children, variant = 'default', ...props }, ref) => {
    const variantClasses = {
      default: 'bg-surface text-foreground border-border',
      destructive: 'bg-danger/10 text-danger-emphasis border-danger/20',
      success: 'bg-success/10 text-success-emphasis border-success/20',
      warning: 'bg-warning/10 text-warning-emphasis border-warning/20 border-l-4 border-l-warning',
      info: 'bg-info/10 text-info border-info/20',
    };

    return (
      <div
        ref={ref}
        className={cn('relative w-full rounded-lg border p-4', variantClasses[variant], className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);

export const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  return (
    <p ref={ref} className={cn('leading-none font-medium tracking-tight', className)} {...props} />
  );
});

AlertTitle.displayName = 'AlertTitle';

export const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  return <p ref={ref} className={cn('text-muted-foreground mt-1 text-sm', className)} {...props} />;
});

AlertDescription.displayName = 'AlertDescription';

Alert.displayName = 'Alert';
